import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  output,
  SimpleChanges,
} from '@angular/core';
import { ZiroFormsModule } from '../../ziro-forms.module';
import { TranslateModule } from '@ngx-translate/core';
import { ZiroFormAbstractDirective } from '../../ziro-form-abstract.directive';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../ziro-forms-models';
import { Observable, of, Subscription } from 'rxjs';
import { ButtonSizes, ButtonStyles, ButtonTypes, ZiroButtonComponent } from '../../../button/button.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ZiroTimeSelectComponent, ZiroTimeSelectConfig } from './select/ziro-time-select.component';

export interface TimeRange {
  start: string;
  end: string;
}

@Component({
  selector: 'ziro-time-range-form',
  templateUrl: './ziro-time-range-form.component.html',
  imports: [ZiroFormsModule, TranslateModule, ZiroButtonComponent, ZiroTimeSelectComponent],
  standalone: true,
})
export class ZiroTimeRangeFormComponent
  extends ZiroFormAbstractDirective<TimeRange>
  implements AfterViewInit, OnChanges, OnDestroy
{
  private _changeDetectorRef = inject(ChangeDetectorRef);

  showDelete = input<boolean>(true);
  canDelete = input<boolean>(true);
  dataAutomation = input<string>('');
  isOverlapping = input<boolean>(false);

  timeIntervalMinutes = input<number | null>(null);
  isRequired = input<boolean>(true);

  @Input() isFormSubmitted: boolean;

  deleteClicked = output<void>();
  validationChange = output<ZiroFormsValidationState>();

  formConfig: ZiroFormConfig = {
    fields: {
      start: {
        label: () => '',
        dataAutomation: 'time-range-start',
        componentConfig: new ZiroTimeSelectConfig(),
        required: () => this.isRequired(),
        validation: [
          {
            validator: (startVal: string, endVal: string): ZiroFormsValidationState => {
              if (!startVal || !endVal) {
                return ZiroFormsValidationState.INVALID;
              }
              return this._toMinutes(startVal) < this._toMinutes(endVal)
                ? ZiroFormsValidationState.VALID
                : ZiroFormsValidationState.INVALID;
            },
            message: 'tkey;validators.global.error.time_range.incorrect_time_range',
            injectValuesFromFields: ['end'],
          },
          {
            validator: (): ZiroFormsValidationState =>
              this.isOverlapping() ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID,
            message: 'tkey;validators.global.error.time_range.overlapping_ranges',
          },
        ],
      },
      end: {
        label: () => '',
        dataAutomation: 'time-range-end',
        componentConfig: new ZiroTimeSelectConfig(),
        required: () => this.isRequired(),
        validation: [
          {
            validator: (endVal: string, startVal: string): ZiroFormsValidationState => {
              if (!startVal || !endVal) {
                return ZiroFormsValidationState.INVALID;
              }

              return this._toMinutes(endVal) > this._toMinutes(startVal)
                ? ZiroFormsValidationState.VALID
                : ZiroFormsValidationState.INVALID;
            },
            message: 'tkey;validators.global.error.time_range.incorrect_time_range',
            injectValuesFromFields: ['start'],
          },
          {
            validator: (): ZiroFormsValidationState =>
              this.isOverlapping() ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID,
            message: 'tkey;validators.global.error.time_range.overlapping_ranges',
          },
        ],
      },
    },
  };

  private _validationSub = new Subscription();

  constructor() {
    super();
    effect(() => {
      this.isOverlapping();
      this.timeIntervalMinutes();
      Object.values(this.fieldChannels ?? {}).forEach((c) => c.validateSource.next());
      this._changeDetectorRef.detectChanges();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes['isFormSubmitted']?.currentValue) {
      this.fieldComponents?.forEach((field) => {
        field.showValidation = true;
      });
      this._changeDetectorRef.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    const sub = this.ziroFormsUpdate$.subscribe((update) => {
      this.validationChange.emit(update.valid as ZiroFormsValidationState);
    });
    this._validationSub.add(sub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._validationSub.unsubscribe();
  }

  protected submit(): Observable<null> {
    return of(null);
  }

  private _toMinutes(value: string): number {
    const [hours, minutes] = value.split(':').map(Number);
    return hours * 60 + minutes;
  }

  protected readonly ButtonSizes = ButtonSizes;
  protected readonly ButtonStyles = ButtonStyles;
  protected readonly ButtonTypes = ButtonTypes;
  protected readonly ZiroIcons = ZiroIcons;
}
